import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ClientChannelRow, clientQuery, clientTitle, type Client } from '@/entities/client'
import { cn } from '@/shared/lib/cn'
import { pluralizeRu } from '@/shared/lib/format'
import { Icon, SectionCard, Skeleton, WidgetError } from '@/shared/ui'
import s from './BookingClientCard.module.css'

/**
 * Секция «Профиль гостя» карточки брони.
 *
 * ⚠️ ДАННЫЕ БЕРУТСЯ НЕ ИЗ БРОНИ. Бронь несёт только `clientId`, а профиль читается из
 * `GET /clients/{id}` — той же ручкой, что и раздел «Гости». Раньше бронь возила копию профиля, и
 * копия была беднее оригинала: каналы приезжали как `Messenger` (шесть видов вместо двенадцати, без
 * различения «телефон в Telegram» и «ник в Telegram»), атрибуции не было вовсе, а статистика
 * значилась обязательной, хотя clients-service броней не знает в принципе (§1).
 *
 * Что здесь показывается и почему именно это (§3): имя (оно МОЖЕТ БЫТЬ ПУСТЫМ), каналы С
 * АТРИБУЦИЕЙ, число вариантов написания и присутствие в других компаниях. Правка живёт в разделе
 * «Гости» — карточка в брони отвечает на вопрос «с кем мы имеем дело», а не «поправь профиль».
 */
export function BookingClientCard({ clientId }: { clientId: string }) {
  const client = useQuery(clientQuery(clientId))

  return (
    <SectionCard
      className={s.card}
      eyebrow="Профиль гостя"
      aside={
        /*
         * Ссылка ведёт В КАРТОЧКУ ЭТОГО ГОСТЯ. До появления раздела она вела просто в «Гости»: раздел
         * был помечен `roadmap` и рисовал «Пока в планах», то есть адрес с `?client=` обещал бы
         * конкретного человека, за которым ничего нет. Раздел построен — обещание обеспечено, и
         * изменилась ровно одна строка, как и было записано.
         *
         * Уходим ОБЪЕКТОМ, а не функцией: объект сбрасывает `booking`, то есть модалка брони
         * закрывается. Так и надо, мы уходим на другой экран (правило — в `app/router/search.ts`).
         */
        <Link
          className={s.profile}
          to="/studio/$section"
          params={{ section: 'clients' }}
          search={{ client: clientId }}
          title="Открыть карточку гостя в разделе «Гости»"
        >
          Открыть
          <Icon name="external" size={11} strokeWidth={2} />
        </Link>
      }
    >
      <div className={s.body}>
        {/*
         * ⚠️ ДАННЫЕ ПРОВЕРЯЮТСЯ РАНЬШЕ ОШИБКИ. Упавший ФОНОВЫЙ перезапрос (у нас включён
         * `refetchOnWindowFocus`) выставляет `error` и ОСТАВЛЯЕТ `data` целой: порядок «ошибка →
         * данные» подменял бы живой профиль экраном «Сервер не смог ответить» у того, кто просто
         * вернулся во вкладку. Это записано в HANDOFF отдельным пунктом и соблюдено в этом же
         * файле строкой выше (`BookingModal`) и в ленте переписки.
         */}
        {client.data ? (
          <ClientBody client={client.data} />
        ) : client.isPending ? (
          <div className={s.loading}>
            <Skeleton width="60%" height={18} radius={6} />
            <Skeleton width="100%" height={44} radius={8} />
            <Skeleton width="100%" height={44} radius={8} />
          </div>
        ) : (
          <WidgetError error={client.error} retry={() => void client.refetch()} />
        )}
      </div>
    </SectionCard>
  )
}

function ClientBody({ client }: { client: Client }) {
  const { title, namelessHint, mono } = clientTitle(client)
  const variants = client.names.length

  return (
    <>
      <div className={s.head}>
        {/* Моноширинный — только когда заголовок это НОМЕР или id: цифры так читаются разряд за
            разрядом. Для запасной фразы «Гость без имени» это был бы просто странный шрифт. */}
        <span className={s.name} data-mono={mono ? 'true' : undefined}>
          {title}
        </span>

        <span className={s.notes}>
          {/*
           * Безымянный гость — штатный случай (§3), а не сбой: телефон уже стоит заголовком, и
           * рядом честно сказано, чего не хватает.
           *
           * Ссылка «добавить имя» — та самая, которую просит §3. Она появилась только теперь: пока
           * имена нельзя было править, вести ей было некуда, и кнопка обещала бы действие, которого
           * нет. Ведёт в карточку ЭТОГО гостя, где написания и заводятся.
           */}
          {namelessHint ? (
            <span className={s.nameless}>
              {namelessHint} —{' '}
              <Link
                className={s.addName}
                to="/studio/$section"
                params={{ section: 'clients' }}
                search={{ client: client.id }}
              >
                добавить имя
              </Link>
            </span>
          ) : null}

          {/* Имена КОПЯТСЯ, а не заменяются (§2.4) — и это стоит сказать: «Петров И.В.» из старой
              выгрузки и полное ФИО из разговора описывают одного человека. */}
          {variants > 1 ? (
            <span className={s.note}>
              ещё {variants - 1} {pluralizeRu(variants - 1, ['написание', 'написания', 'написаний'])}{' '}
              имени
            </span>
          ) : null}

          {/*
           * Гость общий, базы у компаний свои (§2.1). Названий чужих компаний нет ни в контракте, ни
           * здесь: спека просит показывать их числом, и это единственное безопасное — назвать
           * компанию значило бы рассказать менеджеру, у кого ещё живёт его гость.
           */}
          {client.otherCompaniesCount > 0 ? (
            <span className={s.note} title="Гость общий для всей системы: карточка одна, базы у компаний свои">
              есть ещё в {client.otherCompaniesCount}{' '}
              {pluralizeRu(client.otherCompaniesCount, ['компании', 'компаниях', 'компаниях'])}
            </span>
          ) : null}
        </span>
      </div>

      {client.contacts.length === 0 ? (
        <p className={s.empty}>Каналов связи нет — с гостем не через что связаться.</p>
      ) : (
        <ul className={s.channels}>
          {client.contacts.map((contact) => (
            <ClientChannelRow key={contact.identityId} contact={contact} />
          ))}
        </ul>
      )}

      {/*
       * Статистика — СКЛЕЙКА ГЕЙТВЕЯ с bookings-service, и потому необязательная: clients-service
       * броней не хранит (§1). Нет склейки — нет полосы; выдумывать её из счётчика подтверждений
       * связи нельзя, тот считает ещё и строки импорта, и переходы в бота (§4).
       */}
      {client.stats ? (
        <div className={s.stats}>
          <div className={s.stat}>
            <div className={s.statLabel}>визитов</div>
            <div className={s.statValue}>{client.stats.visits}</div>
          </div>
          <div className={s.stat}>
            <div className={s.statLabel}>ночей</div>
            <div className={s.statValue}>{client.stats.nights}</div>
          </div>
          <div className={s.stat}>
            <div className={s.statLabel}>потрачено</div>
            <div className={cn(s.statValue, s.statMoney)}>{client.stats.spentLabel}</div>
          </div>
        </div>
      ) : null}
    </>
  )
}
